import { useCallback } from 'react';

import { fetchEndpointResult } from 'api';
import { useCartContext, useToastContext } from 'contexts';
import type { Cart, CartRequest, VariantMetaData } from 'models/api';
import type { VariantId } from 'models/product';
import type { CustomizationId, DiscountCodeNumber, WithRequired } from 'types';
import { formatCartForRequest } from 'utils/business-logic';
import { filterTruthy } from 'utils/collection';
import { asArray, is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { parseMarkdown } from 'utils/string';

import { usePath } from './navigation';
import { useGlobalLinks } from './useSitecoreContext';

interface VariantData {
	customizationId?: CustomizationId;
	metaData?: VariantMetaData;
	quantity?: number;
	variantId: VariantId;
}

type OptimisticRowUpdate = WithRequired<VariantData, 'quantity'>;

function mergeBasketRowsQuantity(
	cart: Cart,
	rows: OptimisticRowUpdate[],
): Cart {
	if (cart.basketRows.length === 0 || rows.length === 0) {
		return cart;
	}
	return {
		...cart,
		basketRows: cart.basketRows.map((basketRow) => ({
			...basketRow,
			quantity:
				rows.find(
					(row) =>
						row.variantId === basketRow.variantId &&
						row.customizationId === basketRow.customization?.customization?.id,
				)?.quantity ?? basketRow.quantity,
		})),
	};
}

export function useCart() {
	const { t } = useI18n();
	const path = usePath();
	const { cart: cartPagePath } = useGlobalLinks();

	const { sendToast } = useToastContext();
	const { cart, openAddedProductPopover, updateCart, updateTotalQuantity } =
		useCartContext();

	const updateVariantFull = useCallback(
		async (
			variantsData: VariantData | VariantData[],
			options: {
				setDistinctValue: boolean;
			},
		) => {
			const res = await fetchEndpointResult({
				name: 'PostCartVariants',
				body: asArray(variantsData).map((variant) => ({
					quantity: variant.quantity ?? 1,
					...variant,
				})),
				queryParams: {
					setDistinctValue: options.setDistinctValue ? 'true' : 'false',
				},
			});

			if (!res.isSuccess() || !res.value) {
				sendToast({
					message: t('general_critical_error_text'),
					variant: 'error',
				});
				throw new Error('update variant full error');
			}

			return res.value;
		},
		[sendToast, t],
	);

	const updateVariantMini = useCallback(
		async (
			variantsData: VariantData | VariantData[],
			options: {
				returnAccessories: boolean;
				returnAddedProducts: boolean;
				setDistinctValue: boolean;
				showPopover: boolean;
				showToast: boolean;
			},
		) => {
			const res = await fetchEndpointResult({
				name: 'PostCartMiniVariants',
				body: asArray(variantsData).map((variant) => ({
					quantity: variant.quantity ?? 1,
					...variant,
				})),
				queryParams: {
					returnAccessories: options.returnAccessories ? 'true' : 'false',
					returnAddedProducts: options.returnAddedProducts ? 'true' : 'false',
					setDistinctValue: options.setDistinctValue ? 'true' : 'false',
				},
			});

			if (!res.isSuccess() || !res.value) {
				sendToast({
					message: t('general_critical_error_text'),
					variant: 'error',
				});
				throw new Error('update variant mini error');
			}

			const isSuccess = res.value.success;

			if (options.showToast) {
				const message = is.arrayWithLength(res.value.addedProducts)
					? parseMarkdown(
							t('variant_added_to_cart_text', {
								productTitle: res.value.addedProducts
									.map(({ title }) => title)
									.join(', '),
							}),
						)
					: res.value.message;

				if (message) {
					sendToast({
						message,
						variant: isSuccess ? 'success' : 'error',
						link: cartPagePath,
						linkText: t('continue_to_cart'),
					});
				}
			}

			if (!isSuccess) {
				throw new Error('Failed to add product to cart');
			}

			if (options.showPopover) {
				openAddedProductPopover(res.value);
			}

			return res.value;
		},
		[cartPagePath, openAddedProductPopover, sendToast, t],
	);

	const removeFromCart = useCallback(
		async (
			variantId: VariantId,
			customizationId: CustomizationId | undefined,
		) => {
			const newCart = await updateVariantFull(
				{ variantId, quantity: 0, customizationId },
				{ setDistinctValue: true },
			);
			updateCart(newCart);
			updateTotalQuantity(newCart.totalQty);
			return newCart;
		},
		[updateVariantFull, updateCart, updateTotalQuantity],
	);

	// TODO: Look at integrating sendAddToCartEvent here.
	const addToCart = useCallback(
		async (
			variantsData: VariantData | VariantData[],
			{
				optimisticQuantityUpdate = false,
				returnAccessories = false,
				returnAddedProducts = false,
				setDistinctValue = false,
				showPopover = false,
				showToast = false,
			}: {
				optimisticQuantityUpdate?: boolean;
				returnAccessories?: boolean;
				returnAddedProducts?: boolean;
				setDistinctValue?: boolean;
				showPopover?: boolean;
				showToast?: boolean;
			},
		) => {
			if (optimisticQuantityUpdate && cart) {
				updateCart(
					mergeBasketRowsQuantity(
						cart,
						filterTruthy(asArray(variantsData), 'quantity'),
					),
				);
			}
			if (path.startsWith(cartPagePath)) {
				const newCart = await updateVariantFull(variantsData, {
					setDistinctValue,
				});
				updateCart(newCart);
				updateTotalQuantity(newCart.totalQty);
				return newCart;
			}
			const newMiniCart = await updateVariantMini(variantsData, {
				setDistinctValue,
				showToast,
				showPopover,
				returnAddedProducts,
				returnAccessories,
			});
			updateTotalQuantity(newMiniCart.totalQty);
			return newMiniCart;
		},
		[
			cart,
			cartPagePath,
			path,
			updateCart,
			updateTotalQuantity,
			updateVariantFull,
			updateVariantMini,
		],
	);

	const updateDiscountCode = useCallback(
		async (discountCode: DiscountCodeNumber) => {
			if (!cart) {
				throw new Error('Cart is undefined');
			}
			const updatedCart: CartRequest = formatCartForRequest(cart, {
				cartDiscountCode: discountCode,
			});
			const res = await fetchEndpointResult({
				name: 'PutCart',
				pathParams: { cartId: cart.id },
				body: updatedCart,
			});

			if (!res.isSuccess() || !res.value) {
				sendToast({
					message: t('general_critical_error_text'),
					variant: 'error',
				});
				throw new Error('discount code error');
			}

			updateCart(res.value);
			updateTotalQuantity(res.value.totalQty);
			const discountError = res.value?.errorList?.find(
				(error) =>
					error.type === 'DiscountCodeNotFound' ||
					error.type === 'DiscountExpired' ||
					error.type === 'DiscountInvalid',
			);
			const businessLogicErrors = res.value?.errorList?.filter(
				(error) =>
					error.type === 'DiscountCountError' ||
					error.type === 'DiscountCombinationInvalid',
			);
			return { discountError, businessLogicErrors };
		},
		[cart, sendToast, t, updateCart, updateTotalQuantity],
	);

	const addDiscountCode = useCallback(
		async (discountCode: DiscountCodeNumber) =>
			updateDiscountCode(discountCode),
		[updateDiscountCode],
	);

	const removeDiscountCode = useCallback(
		async (discountCode: DiscountCodeNumber) => {
			await updateDiscountCode(discountCode);
		},
		[updateDiscountCode],
	);

	return { addToCart, removeFromCart, addDiscountCode, removeDiscountCode };
}
